/* =====================================================
   Complete the Look — PDP section + Mini-Cart strip
   Depends on: window.theme.Flickity (vendor.js via theme.js),
               window.theme.formatMoney, window.theme.routes
   ===================================================== */

(function () {
  'use strict';

  const DEBUG = false;
  const log = (...args) => DEBUG && console.log('[CTL]', ...args);

  /* ---- Shared helpers ---- */

  function money(cents) {
    return window.theme.formatMoney(cents, window.theme.moneyFormat);
  }

  /* ---- Cart drawer refresh ---- */
  const CTLCart = {
    refresh() {
      // window.cart is the Palo Alto global cart instance (set in theme.js)
      if (window.cart && typeof window.cart.getCart === 'function') {
        window.cart.getCart();
      } else {
        // Fallback: dispatch theme:cart:added so the theme opens/refreshes the drawer
        document.dispatchEvent(new CustomEvent('theme:cart:added', { bubbles: true }));
      }
    },
  };

  async function cartAdd(variantId) {
    const url = (window.theme.routes && window.theme.routes.cart_add_url)
      ? window.theme.routes.cart_add_url + '.js'
      : '/cart/add.js';
    const resp = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    });
    const data = await resp.json();
    return { ok: resp.ok, data };
  }

  async function getCart() {
    const resp = await fetch('/cart.js');
    return resp.json();
  }

  /* ================================================================
     CTL Card Controller
     State machine:
       idle          – default, CTA functional
       viewing       – current PDP product; VIEWING pill shown, CTA functional
       in-cart       – product in cart; button shows "{SIZE} - ADDED" or "✓ ADDED"
       viewing-added – both viewing AND in cart
       added         – just added by user (same appearance as in-cart)
       sold-out      – all variants unavailable
       error         – add-to-cart failed
  ================================================================ */

  class CTLCard {
    constructor(root) {
      this.root = root;
      this.btn = root.querySelector('[data-ctl-btn]');
      this.dropdown = root.querySelector('[data-ctl-dropdown]');
      this.errorEl = root.querySelector('[data-ctl-error]');
      this.badgeEl = root.querySelector('[data-ctl-badge]');

      const raw = root.querySelector('[data-ctl-product-json]');
      this.product = raw ? JSON.parse(raw.textContent) : null;

      this._pending = false;
      this._dropdownOpen = false;

      if (!this.btn || !this.product) return;

      this._bind();
    }

    _bind() {
      this.btn.addEventListener('click', (e) => this._onBtnClick(e));

      if (this.dropdown) {
        this.dropdown.querySelectorAll('[data-ctl-variant-id]').forEach((item) => {
          item.addEventListener('click', (e) => this._onVariantClick(e));
          item.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._onVariantClick(e); }
            if (e.key === 'Escape') this._closeDropdown();
          });
        });

        document.addEventListener('click', (e) => {
          if (this._dropdownOpen && !this.root.contains(e.target)) this._closeDropdown();
        });

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this._dropdownOpen) this._closeDropdown();
        });
      }
    }

    _onBtnClick(e) {
      e.preventDefault();
      const state = this.root.dataset.ctlState;
      // Block only when already added/in-cart — "viewing" is now functional
      if (state === 'in-cart' || state === 'added' || state === 'viewing-added' || this._pending) return;

      const variants = this.product.variants.filter((v) => !this.product.hide_oos || v.available);
      if (variants.length === 0) return;

      // Single available variant — direct add
      if (variants.length === 1 && variants[0].available) {
        this._addToCart(variants[0].id, variants[0].title);
        return;
      }

      // Multi-variant — toggle dropdown
      if (this._dropdownOpen) {
        this._closeDropdown();
      } else {
        this._openDropdown();
      }
    }

    _onVariantClick(e) {
      const item = e.currentTarget;
      if (item.disabled) return;
      const variantId = parseInt(item.dataset.ctlVariantId, 10);
      const title = item.textContent.trim();
      this._closeDropdown();
      this._addToCart(variantId, title);
    }

    _openDropdown() {
      if (!this.dropdown) return;
      this._dropdownOpen = true;
      this.dropdown.setAttribute('aria-hidden', 'false');
      this.btn.setAttribute('aria-expanded', 'true');
      const first = this.dropdown.querySelector('[data-ctl-variant-id]:not(:disabled)');
      if (first) first.focus();
    }

    _closeDropdown() {
      if (!this.dropdown) return;
      this._dropdownOpen = false;
      this.dropdown.setAttribute('aria-hidden', 'true');
      this.btn.setAttribute('aria-expanded', 'false');
    }

    async _addToCart(variantId, variantTitle) {
      if (this._pending) return;
      this._pending = true;
      this._setLoading(true);
      this._clearError();

      log('Adding variant', variantId);

      try {
        const { ok, data } = await cartAdd(variantId);
        if (ok) {
          // Preserve VIEWING pill if card was in viewing state
          const wasViewing = this.root.dataset.ctlState === 'viewing';
          this._setState(wasViewing ? 'viewing-added' : 'added', variantTitle);
          this._announce(`${this.product.title} — ${variantTitle} added to cart`);
          document.dispatchEvent(new CustomEvent('ctl:added', { detail: { productId: this.product.id, variantId } }));
          // Refresh the theme cart drawer (re-fetch rendered section, update DOM, open drawer)
          CTLCart.refresh();
        } else {
          const msg = data.description || data.message || 'Could not add to cart.';
          this._setState('error', null, msg);
        }
      } catch (err) {
        log('Cart add error', err);
        this._setState('error', null, 'Something went wrong. Please try again.');
      } finally {
        this._pending = false;
        this._setLoading(false);
      }
    }

    _setLoading(on) {
      this.root.classList.toggle('ctl__card--loading', on);
      if (this.btn) this.btn.setAttribute('aria-disabled', on ? 'true' : 'false');
    }

    _setState(state, variantTitle, errorMsg) {
      this.root.dataset.ctlState = state;
      this.root.classList.remove('ctl__card--added', 'ctl__card--in-cart', 'ctl__card--error');
      // ctl__card--viewing is NOT removed here — it is permanent for the current PDP product
      // It only changes when viewing transitions to viewing-added

      const isViewing = state === 'viewing' || state === 'viewing-added';
      const isAdded = state === 'added' || state === 'in-cart' || state === 'viewing-added';

      // Badge: "VIEWING" pill — only visible via CSS when ctl__card--viewing class present
      if (isViewing) {
        this.root.classList.add('ctl__card--viewing');
        // Show VIEWING only when NOT in cart; if also in cart, show IN CART instead
        if (this.badgeEl) {
          this.badgeEl.textContent = state === 'viewing-added' ? 'IN CART' : 'VIEWING';
        }
      }

      // Button + badge state
      if (isAdded) {
        if (state === 'viewing-added') this.root.classList.add('ctl__card--in-cart');
        else this.root.classList.add(state === 'added' ? 'ctl__card--added' : 'ctl__card--in-cart');

        if (this.badgeEl && !isViewing) {
          this.badgeEl.textContent = 'IN CART';
        }

        if (this.btn) {
          this.btn.querySelector('[data-ctl-btn-text]').textContent = this._addedLabel(variantTitle);
          this.btn.setAttribute('aria-disabled', 'true');
          this.btn.classList.add('ctl__btn--filled');
        }
      } else if (state === 'error') {
        this.root.classList.add('ctl__card--error');
        if (this.errorEl) this.errorEl.textContent = errorMsg || 'Error adding to cart.';
      }
      // viewing / idle: button text unchanged (already correct from Liquid render)
    }

    // Returns button label for added/in-cart states
    _addedLabel(variantTitle) {
      const isOneSize = this.product?.variants?.length === 1;
      if (isOneSize) return '✓ ADDED';
      const short = variantTitle ? variantTitle.split(' / ')[0].trim() : '';
      return short && short !== 'Default Title' ? `${short} - ADDED` : 'ADDED';
    }

    _clearError() {
      this.root.classList.remove('ctl__card--error');
      if (this.errorEl) this.errorEl.textContent = '';
    }

    _announce(msg) {
      const region = this.root.closest('[data-ctl-section], [data-ctl-minicart]')
        ?.querySelector('[data-ctl-live-region]');
      if (region) {
        region.textContent = '';
        requestAnimationFrame(() => { region.textContent = msg; });
      }
    }
  }

  /* ================================================================
     PDP Section
  ================================================================ */

  class CTLSection {
    constructor(section) {
      this.section = section;
      this.viewport = section.querySelector('[data-ctl-viewport]');
      this.navPrev = section.querySelector('[data-ctl-nav-prev]');
      this.navNext = section.querySelector('[data-ctl-nav-next]');
      this.dotsContainer = section.querySelector('[data-ctl-dots]');
      this.progressTrack = section.querySelector('[data-ctl-progress]');
      this.progressFill = this.progressTrack?.querySelector('.ctl__progress-fill');
      this.flkty = null;

      this._initCards();
      this._initFlickity();
      this._hydrateCartStates();
      this._bindCartEvents();
    }

    _initCards() {
      this.section.querySelectorAll('[data-ctl-card]').forEach((el) => {
        el._ctlInstance = new CTLCard(el);
      });
    }

    _initFlickity() {
      if (!this.viewport || !window.theme.Flickity) return;

      const infinite = this.section.dataset.ctlInfinite === 'true';
      const autoplay = this.section.dataset.ctlAutoplay === 'true';
      const autoplaySpeed = parseInt(this.section.dataset.ctlAutoplaySpeed || '4', 10) * 1000;
      const showArrows = this.section.dataset.ctlShowArrows !== 'false';
      const showDots = this.section.dataset.ctlShowDots === 'true';

      this.flkty = new window.theme.Flickity(this.viewport, {
        cellSelector: '.ctl__slide',
        cellAlign: 'left',
        contain: true,
        pageDots: false,
        prevNextButtons: false,
        wrapAround: infinite,
        autoPlay: autoplay ? autoplaySpeed : false,
        accessibility: true,
        draggable: true,
        freeScroll: false,
        watchCSS: false,
        on: {
          ready: () => { requestAnimationFrame(() => { this.flkty.resize(); this._updateNav(); }); },
          change: () => this._updateNav(),
        },
      });

      if (showArrows) {
        this.navPrev?.addEventListener('click', () => this.flkty.previous());
        this.navNext?.addEventListener('click', () => this.flkty.next());
      } else {
        this.navPrev?.classList.add('ctl__nav-btn--hidden');
        this.navNext?.classList.add('ctl__nav-btn--hidden');
      }

      if (showDots && this.dotsContainer) {
        this._buildDots();
        this.flkty.on('change', (i) => this._updateDots(i));
      }

      this.viewport.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.flkty.previous();
        if (e.key === 'ArrowRight') this.flkty.next();
      });
    }

    _updateNav() {
      if (!this.flkty) return;
      const cells = this.flkty.cells.length;
      const isDesktop = window.innerWidth >= 768;
      const visibleCount = parseFloat(
        isDesktop
          ? (this.section.dataset.ctlItemsDesktop || '3')
          : (this.section.dataset.ctlItemsMobile || '1.5')
      );
      const allFit = cells <= visibleCount;
      if (this.navPrev) this.navPrev.classList.toggle('ctl__nav-btn--hidden', allFit);
      if (this.navNext) this.navNext.classList.toggle('ctl__nav-btn--hidden', allFit);

      if (!allFit) {
        const atStart = this.flkty.selectedIndex === 0;
        const atEnd = this.flkty.selectedIndex >= cells - 1;
        if (this.navPrev) this.navPrev.disabled = atStart && !this.flkty.options.wrapAround;
        if (this.navNext) this.navNext.disabled = atEnd && !this.flkty.options.wrapAround;
      }
      this._updateProgress();
    }

    _updateProgress() {
      if (!this.progressTrack || !this.flkty) return;
      const cells = this.flkty.cells.length;
      const isDesktop = window.innerWidth >= 768;
      const visibleCount = parseFloat(
        isDesktop
          ? (this.section.dataset.ctlItemsDesktop || '3')
          : (this.section.dataset.ctlItemsMobile || '1.5')
      );
      if (cells <= visibleCount) { this.progressTrack.hidden = true; return; }
      this.progressTrack.hidden = false;
      const thumbPct = Math.min(100, (visibleCount / cells) * 100);
      const scrollable = Math.max(1, cells - Math.floor(visibleCount));
      const leftPct = (this.flkty.selectedIndex / scrollable) * (100 - thumbPct);
      if (this.progressFill) {
        this.progressFill.style.width = `${thumbPct}%`;
        this.progressFill.style.left = `${leftPct}%`;
      }
    }

    _buildDots() {
      if (!this.flkty || !this.dotsContainer) return;
      this.dotsContainer.innerHTML = '';
      const count = this.flkty.cells.length;
      for (let i = 0; i < count; i++) {
        const btn = document.createElement('button');
        btn.className = 'dot' + (i === 0 ? ' is-selected' : '');
        btn.setAttribute('aria-label', `Slide ${i + 1}`);
        btn.addEventListener('click', () => this.flkty.select(i));
        this.dotsContainer.appendChild(btn);
      }
    }

    _updateDots(index) {
      if (!this.dotsContainer) return;
      this.dotsContainer.querySelectorAll('.dot').forEach((d, i) => {
        d.classList.toggle('is-selected', i === index);
      });
    }

    _bindCartEvents() {
      const handler = () => this._refreshCardStates();
      document.addEventListener('theme:cart:loaded', handler);
      document.addEventListener('theme:cart:added', handler);
      document.addEventListener('theme:cart:update', handler);
    }

    async _refreshCardStates() {
      try {
        const cart = await getCart();
        const cartItemMap = new Map(cart.items.map((i) => [i.product_id, i]));
        this.section.querySelectorAll('[data-ctl-card]').forEach((card) => {
          const productId = parseInt(card.dataset.ctlProductId, 10);
          if (!productId) return;
          const currentState = card.dataset.ctlState;
          // Don't interrupt a pending add operation
          if (card.classList.contains('ctl__card--loading')) return;

          const cartItem = cartItemMap.get(productId);
          const isViewing = currentState === 'viewing' || currentState === 'viewing-added';
          const ctrl = card._ctlInstance;
          if (!ctrl) return;

          if (cartItem) {
            const newState = isViewing ? 'viewing-added' : 'in-cart';
            ctrl._setState(newState, cartItem.variant_title);
          } else {
            // Product removed from cart — revert to viewing or idle
            if (isViewing) {
              ctrl._setState('viewing');
              // _setState('viewing') intentionally skips button reset — do it here
              const isSingle = ctrl.product?.variants?.length === 1;
              const btnText = card.querySelector('[data-ctl-btn-text]');
              if (btnText) btnText.textContent = isSingle ? 'ADD TO CART +' : 'SELECT SIZE +';
              const btn = card.querySelector('[data-ctl-btn]');
              if (btn) {
                btn.removeAttribute('aria-disabled');
                btn.removeAttribute('disabled');
                btn.classList.remove('ctl__btn--filled');
              }
            } else if (currentState === 'added' || currentState === 'in-cart') {
              // Reset button text manually since _setState only handles non-idle states
              card.dataset.ctlState = 'idle';
              card.classList.remove('ctl__card--added', 'ctl__card--in-cart');
              const btnText = card.querySelector('[data-ctl-btn-text]');
              if (btnText) {
                const isSingle = ctrl.product?.variants?.length === 1;
                btnText.textContent = isSingle ? 'ADD TO CART +' : 'SELECT SIZE +';
              }
              const btn = card.querySelector('[data-ctl-btn]');
              if (btn) {
                btn.removeAttribute('aria-disabled');
                btn.removeAttribute('disabled');
                btn.classList.remove('ctl__btn--filled');
              }
            }
          }
        });
      } catch (e) {
        log('Cart state refresh error', e);
      }
    }

    async _hydrateCartStates() {
      try {
        const cart = await getCart();
        const cartItemMap = new Map(cart.items.map((i) => [i.product_id, i]));
        this.section.querySelectorAll('[data-ctl-card]').forEach((card) => {
          const productId = parseInt(card.dataset.ctlProductId, 10);
          if (!productId) return;
          const currentState = card.dataset.ctlState;
          // Skip if already showing an added state
          if (currentState === 'added' || currentState === 'viewing-added') return;
          const cartItem = cartItemMap.get(productId);
          if (cartItem) {
            const ctrl = card._ctlInstance;
            if (!ctrl) return;
            const newState = currentState === 'viewing' ? 'viewing-added' : 'in-cart';
            ctrl._setState(newState, cartItem.variant_title);
          }
        });
      } catch (e) {
        log('Cart hydration error', e);
      }
    }
  }

  /* ================================================================
     Mini-Cart Strip
  ================================================================ */

  class CTLMinicart {
    constructor(container) {
      this.container = container;
      this._cardsContainer = container.querySelector('[data-ctl-mc-cards]');
      this._navPrev = container.querySelector('[data-ctl-mc-prev]');
      this._navNext = container.querySelector('[data-ctl-mc-next]');
      this._flkty = null;
      this._progressTrack = null;
      this._progressFill = null;
      this._refreshing = false;

      this._upsellData = this._parseUpsellData();
      this.refresh();
    }

    _parseUpsellData() {
      const map = {};
      const drawer = document.querySelector('[data-cart-drawer]');
      const scope = drawer || document;
      scope.querySelectorAll('[data-ctl-item-upsells]').forEach((el) => {
        try {
          const pid = parseInt(el.dataset.ctlItemProductId, 10);
          map[pid] = { products: JSON.parse(el.textContent), country: el.dataset.ctlItemCountry || '' };
        } catch (e) {
          log('Parse error', e);
        }
      });
      return map;
    }

    async _fetchUpsellData() {
      try {
        const resp = await fetch('/?section_id=api-ctl-upsells', { credentials: 'same-origin' });
        const html = await resp.text();
        const el = document.createElement('div');
        el.innerHTML = html;
        const scope = el.querySelector('[data-api-ctl-upsells]') || el;
        const map = {};
        scope.querySelectorAll('[data-ctl-item-upsells]').forEach((node) => {
          try {
            const pid = parseInt(node.dataset.ctlItemProductId, 10);
            map[pid] = { products: JSON.parse(node.textContent), country: node.dataset.ctlItemCountry || '' };
          } catch (e) {
            log('Parse error', e);
          }
        });
        return map;
      } catch (e) {
        log('Upsell fetch error', e);
        return null;
      }
    }

    async refresh() {
      if (this._refreshing) return;
      this._refreshing = true;

      try {
        const [cart, freshUpsells] = await Promise.all([getCart(), this._fetchUpsellData()]);
        if (freshUpsells && Object.keys(freshUpsells).length > 0) this._upsellData = freshUpsells;
        const { products, country } = this._aggregate(cart);

        if (products.length === 0) {
          this.container.classList.remove('is-visible');
          return;
        }

        this._render(products, country);
        this.container.classList.add('is-visible');
      } catch (e) {
        log('Mini-cart refresh error', e);
      } finally {
        this._refreshing = false;
      }
    }

    _aggregate(cart) {
      if (cart.item_count === 0) return { products: [], country: '' };

      const cartItemMap = new Map(cart.items.map((i) => [i.product_id, i]));

      for (const item of cart.items) {
        const entry = this._upsellData[item.product_id];
        if (!entry || !entry.products || entry.products.length === 0) continue;

        const results = [];
        for (const product of entry.products) {
          const cartItem = cartItemMap.get(product.id);
          results.push({ ...product, _inCart: !!cartItem, _cartVariantTitle: cartItem?.variant_title || null });
          if (results.length >= 3) break;
        }
        return { products: results, country: entry.country || '' };
      }

      return { products: [], country: '' };
    }

    _resolveHeading(country) {
      const template = this.container.dataset.ctlMcHeadingTemplate || 'COMPLETE THE {country} KIT';
      return template.replace('{country}', country).replace(/\s{2,}/g, ' ').trim();
    }

    _render(products, country = '') {
      if (!this._cardsContainer) return;

      const heading = this._resolveHeading(country);
      const headingEl = this.container.querySelector('[data-ctl-mc-heading]');
      if (headingEl) headingEl.textContent = heading;
      this.container.setAttribute('aria-label', heading);

      if (this._flkty) { this._flkty.destroy(); this._flkty = null; }

      // Remove old progress track
      this.container.querySelector('[data-ctl-progress]')?.remove();
      this._progressTrack = null;
      this._progressFill = null;

      const d = this.container.dataset;
      this.container.style.setProperty('--ctl-items-mobile', d.ctlMcItemsMobile || '1.5');
      this.container.style.setProperty('--ctl-items-desktop', d.ctlMcItemsDesktop || '2.5');

      this._cardsContainer.innerHTML = products.map((p) => this._cardHTML(p)).join('');

      const applyInCartStates = () => {
        this._cardsContainer.querySelectorAll('[data-ctl-card][data-ctl-in-cart="true"]').forEach((el) => {
          const ctrl = el._ctlInstance;
          if (ctrl) ctrl._setState('in-cart', el.dataset.ctlCartVariantTitle || null);
        });
      };

      this._cardsContainer.querySelectorAll('[data-ctl-card]').forEach((el) => {
        el._ctlInstance = new CTLCard(el);
      });

      // Apply in-cart states before Flickity init so they're visible on first paint
      applyInCartStates();

      // Progress bar
      const track = document.createElement('div');
      track.className = 'ctl__progress-track';
      track.setAttribute('data-ctl-progress', '');
      track.hidden = true;
      track.innerHTML = '<div class="ctl__progress-fill"></div>';
      this._cardsContainer.after(track);
      this._progressTrack = track;
      this._progressFill = track.querySelector('.ctl__progress-fill');

      // Init Flickity
      if (window.theme.Flickity && products.length > 1) {
        const showArrows = d.ctlMcShowArrows !== 'false';
        const infinite = d.ctlMcInfinite === 'true';
        const autoplay = d.ctlMcAutoplay === 'true';
        const autoplaySpeed = parseInt(d.ctlMcAutoplaySpeed || '4', 10) * 1000;

        this._flkty = new window.theme.Flickity(this._cardsContainer, {
          cellSelector: '.ctl__slide',
          cellAlign: 'left',
          contain: true,
          pageDots: false,
          prevNextButtons: false,
          wrapAround: infinite,
          autoPlay: autoplay ? autoplaySpeed : false,
          accessibility: true,
          draggable: true,
          freeScroll: false,
          watchCSS: false,
          on: {
            ready: () => { requestAnimationFrame(() => { this._flkty.resize(); this._updateNav(); }); },
            change: () => this._updateNav(),
          },
        });

        if (showArrows) {
          if (this._navPrev) this._navPrev.addEventListener('click', () => this._flkty.previous());
          if (this._navNext) this._navNext.addEventListener('click', () => this._flkty.next());
        } else {
          if (this._navPrev) this._navPrev.style.display = 'none';
          if (this._navNext) this._navNext.style.display = 'none';
        }

        this._updateNav();
      } else {
        // No Flickity (single product) — apply in-cart states immediately
        applyInCartStates();
      }
    }

    _cardHTML(product) {
      const image = product.featured_image
        ? `<img src="${this._imgUrl(product.featured_image, 400)}" alt="${this._esc(product.title)}" loading="lazy" width="400" height="533">`
        : `<div style="background:var(--text-alpha-10);width:100%;height:100%;"></div>`;

      const priceFormatted = money(product.price_min);
      const allUnavailable = product.variants.every((v) => !v.available);
      const isSingleVariant = product.variants.length === 1;

      let btnAttr = '';
      let btnText = isSingleVariant ? 'ADD TO CART +' : 'SELECT SIZE +';

      if (allUnavailable) {
        btnAttr = 'aria-disabled="true" disabled';
        btnText = 'SOLD OUT';
      }

      // Only show dropdown for multi-variant, non-OOS products
      const showDropdown = !allUnavailable && !isSingleVariant;
      const variantItems = showDropdown
        ? product.variants.map((v) => {
            const disabled = !v.available ? 'disabled' : '';
            const short = this._shortTitle(v.title);
            const label = !v.available ? `${short} – Sold Out` : short;
            return `<button class="ctl__dropdown__item" data-ctl-variant-id="${v.id}" ${disabled} tabindex="0">${this._esc(label)}</button>`;
          }).join('')
        : '';

      const productJson = JSON.stringify({
        id: product.id,
        title: product.title,
        vendor: product.vendor,
        variants: product.variants,
        hide_oos: false,
      });

      const inCartAttr = product._inCart
        ? `data-ctl-in-cart="true" data-ctl-cart-variant-title="${this._esc(product._cartVariantTitle || '')}"`
        : '';

      return `
        <div class="ctl__slide">
          <div class="ctl__card" data-ctl-card data-ctl-product-id="${product.id}" data-ctl-state="idle" ${inCartAttr}>
            <script type="application/json" data-ctl-product-json>${productJson}<\/script>
            <div class="ctl__card__image-wrap">
              ${image}
              <span class="ctl__card__badge" aria-hidden="true" data-ctl-badge></span>
            </div>
            <div class="ctl__card__info">
              <p class="ctl__card__vendor">${this._esc(product.vendor)}</p>
              <p class="ctl__card__title">${this._esc(product.title)}</p>
              <p class="ctl__card__price">${priceFormatted}</p>
            </div>
            <div class="ctl__card__footer">
              ${showDropdown ? `<div class="ctl__dropdown" data-ctl-dropdown aria-hidden="true" aria-label="Select size for ${this._esc(product.title)}">${variantItems}</div>` : ''}
              <button class="ctl__btn" data-ctl-btn ${btnAttr}
                aria-haspopup="listbox" aria-expanded="false"
                aria-label="${this._esc(btnText)} — ${this._esc(product.title)}">
                <svg class="ctl__btn__spinner" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="2" stroke-dasharray="25 13"/>
                </svg>
                <span class="ctl__btn__text" data-ctl-btn-text>${btnText}</span>
              </button>
              <p class="ctl__card__error" role="alert" data-ctl-error></p>
            </div>
          </div>
        </div>`;
    }

    _updateNav() {
      if (!this._flkty) return;
      const cells = this._flkty.cells.length;
      const isDesktop = window.innerWidth >= 768;
      const visibleCount = parseFloat(
        isDesktop
          ? (this.container.dataset.ctlMcItemsDesktop || '2.5')
          : (this.container.dataset.ctlMcItemsMobile || '1.5')
      );
      const allFit = cells <= visibleCount;
      if (this._navPrev) this._navPrev.classList.toggle('ctl__nav-btn--hidden', allFit);
      if (this._navNext) this._navNext.classList.toggle('ctl__nav-btn--hidden', allFit);

      if (!allFit) {
        const atStart = this._flkty.selectedIndex === 0;
        const atEnd = this._flkty.selectedIndex >= cells - 1;
        if (this._navPrev) this._navPrev.disabled = atStart && !this._flkty.options.wrapAround;
        if (this._navNext) this._navNext.disabled = atEnd && !this._flkty.options.wrapAround;
      }
      this._updateProgress();
    }

    _updateProgress() {
      if (!this._progressTrack || !this._flkty) return;
      const cells = this._flkty.cells.length;
      const isDesktop = window.innerWidth >= 768;
      const visibleCount = parseFloat(
        isDesktop
          ? (this.container.dataset.ctlMcItemsDesktop || '2.5')
          : (this.container.dataset.ctlMcItemsMobile || '1.5')
      );
      if (cells <= visibleCount) { this._progressTrack.hidden = true; return; }
      this._progressTrack.hidden = false;
      const thumbPct = Math.min(100, (visibleCount / cells) * 100);
      const scrollable = Math.max(1, cells - Math.floor(visibleCount));
      const leftPct = (this._flkty.selectedIndex / scrollable) * (100 - thumbPct);
      if (this._progressFill) {
        this._progressFill.style.width = `${thumbPct}%`;
        this._progressFill.style.left = `${leftPct}%`;
      }
    }

    _shortTitle(title) {
      return String(title || '').split(' / ')[0].trim();
    }

    _imgUrl(src, width) {
      if (!src) return '';
      return src.replace(/(\.[^.]+)$/, `_${width}x$1`);
    }

    _esc(str) {
      return String(str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }
  }

  /* ================================================================
     Boot
  ================================================================ */

  let _minicartInstance = null;

  function initMinicart() {
    const tryBoot = () => {
      const el = document.querySelector('[data-cart-drawer] [data-ctl-minicart]');
      if (!el) return;

      if (_minicartInstance && _minicartInstance.container === el) {
        _minicartInstance.refresh();
        return;
      }

      log('CTLMinicart booting');
      _minicartInstance = new CTLMinicart(el);
    };

    document.addEventListener('theme:cart:loaded', tryBoot);
    document.addEventListener('theme:cart:added', tryBoot);
    document.addEventListener('theme:cart:update', tryBoot);
  }

  function init() {
    document.querySelectorAll('[data-ctl-section]').forEach((el) => new CTLSection(el));
    initMinicart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else if ('requestIdleCallback' in window) {
    requestIdleCallback(init, { timeout: 2000 });
  } else {
    setTimeout(init, 0);
  }
})();
